import SessionClient from "./SessionClient"
import generateKey from "./generateKey"
import axios from "axios"
import getResultData from "./getResultData"
import { defaultStorageHelper } from "./StorageHelper"

import { ReturnTypes } from "@sprycore/spry-api-client"
import { getResultFromState, GetStateResult } from "./campaignState"

function combineResults(results: any[]): any {
    const result = results[0]
    for (let x = 1; x < results.length; ++x) {
        const nextResult = results[x]
        Object.keys(nextResult).forEach(k => {
            if (k === "continuationToken") { return }
            if (Array.isArray(result[k]) && Array.isArray(nextResult[k])) {
                result[k] = result[k].concat(nextResult[k])
            }
            else {
                result[k] = nextResult[k]
            }
        })
    }
    if (results.length > 1) { delete result.continuationToken }
    return result
}

export default class SPRYCampaignClient {
    __endpoint
    __headers: { [x: string]: string } = {}
    __followContinuationTokens = false
    storageHelper = defaultStorageHelper

    constructor(endpoint: string, authorization?: string) {
        this.__endpoint = endpoint
        if (authorization) { this.__headers.authorization = authorization }
    }

    setAuthorization(authorization: string | null) {
        if (authorization) {
            this.__headers.authorization = authorization
        }
        else {
            delete this.__headers.authorization
        }
    }

    setFollowContinuationTokens(follow: boolean) {
        this.__followContinuationTokens = follow
    }

    /**
     * Creates a new session and saves the session key to storage
     */
    createSession() {
        return this.recoverSession(generateKey())
    }

    /**
     * Recovers a session based on a session key.
     * If no session key is provided, it is recovered from storage.
     * If no session key is found in storage, a new session is created.
     */
    recoverSession(sessionKey?: string) {
        if (!sessionKey) {
            sessionKey = this.storageHelper.get(`SPRYSessionKey`) || generateKey()
        }
        this.storageHelper.set(`SPRYSessionKey`, sessionKey!)
        return new SessionClient(this, sessionKey!)
    }

    /**
     * Gets the current state of the campaign
     * @returns {Promise<GetStateResult>}
     */
    async getState(): Promise<GetStateResult> {
        const result = await this.call<ReturnTypes.CampaignState>("getCampaignState", {})
        return getResultFromState(result.state, result.metadata)
    }

    async call<T>(method: string, body: any): Promise<T> {
        const resultDatas: T[] = []
        let resultData: T = undefined!
        const follow = this.__followContinuationTokens
        do {
            if (follow && resultData && (resultData as any).continuationToken) {
                body.continuationToken = (resultData as any).continuationToken
            }
            const result = await axios.post(`${this.__endpoint}/${method}`, body || {}, { headers: this.__headers })
            resultData = getResultData<T>(result)
            resultDatas.push(resultData)
        } while (follow && resultData && (resultData as any).continuationToken)
        resultData = combineResults(resultDatas) as T
        return resultData
    }
}